// ProfileWizard — first-time fun-profile onboarding form.
// Questions are now admin-customizable: fetched from GET /api/profile/questions,
// grouped into sections, and submitted via POST /api/employee/profile?token=<token>.
// Supports single-choice, multiple-choice (allow_multiple), free-text and custom answers.

import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Check, X } from "lucide-react";

// ---- Confetti (lightweight, CSS-only) ----
function Confetti() {
  const pieces = Array.from({ length: 60 });
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-[60]" aria-hidden>
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const duration = 2 + Math.random() * 2;
        const colors = ["#39FF14", "#FFD600", "#FF3B30", "#1E88E5", "#FF69B4", "#FFFFFF"];
        const bg = colors[i % colors.length];
        const size = 6 + Math.random() * 8;
        const rot = Math.random() * 360;
        return (
          <span
            key={i}
            className="absolute top-[-20px] block animate-confetti"
            style={{
              left: `${left}%`,
              width: size, height: size * 1.6,
              background: bg,
              transform: `rotate(${rot}deg)`,
              animationDuration: `${duration}s`,
              animationDelay: `${delay}s`,
              border: "1px solid rgba(0,0,0,0.5)",
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate3d(0, 110vh, 0) rotate(720deg); opacity: 0.85; }
        }
        .animate-confetti { animation-name: confetti-fall; animation-timing-function: cubic-bezier(.2,.6,.4,1); animation-iteration-count: 1; animation-fill-mode: forwards; }
      `}</style>
    </div>
  );
}

function ChipButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-2 themed-border px-3 py-2 text-sm text-left transition-all hover:scale-[1.02] active:scale-95 ${active ? "brutal-shadow-sm font-black" : "hover:brutal-shadow-sm"}`}
      style={{
        background: active ? "var(--bq-accent)" : "var(--bq-card-bg)",
        color: "var(--bq-text)",
      }}
    >
      <span className="flex items-center gap-2">
        <span className="flex-1">{children}</span>
        {active && <Check size={14} className="flex-shrink-0"/>}
      </span>
    </button>
  );
}

function isAnswered(q, v) {
  if (q.type === "text") return typeof v === "string" && v.trim() !== "";
  if (q.allow_multiple) return Array.isArray(v) && v.length > 0;
  return typeof v === "string" ? v.trim() !== "" : !!v;
}

function QuestionField({ q, value, onChange }) {
  const [customMode, setCustomMode] = useState(false);

  useEffect(() => {
    if (q.type === "radio" && q.allow_custom && !q.allow_multiple && value && !q.options.includes(value)) {
      setCustomMode(true);
    }
  }, [q, value]);

  if (q.type === "text") {
    return (
      <input
        data-testid={`profile-input-${q.id}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={q.placeholder || ""}
        className="brutal-input text-base"
      />
    );
  }

  // Multiple-choice (checkbox-style chips, value is an array)
  if (q.allow_multiple) {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
    return (
      <div className="space-y-2" data-testid={`profile-options-${q.id}`}>
        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Select all that apply</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {q.options.map((opt) => (
            <ChipButton key={opt} active={arr.includes(opt)} onClick={() => toggle(opt)}>{opt}</ChipButton>
          ))}
        </div>
      </div>
    );
  }

  // Single-choice radio chips
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`profile-options-${q.id}`}>
      {q.options.map((opt) => (
        <ChipButton
          key={opt}
          active={value === opt && !customMode}
          onClick={() => { setCustomMode(false); onChange(opt); }}
        >
          {opt}
        </ChipButton>
      ))}
      {q.allow_custom && (
        <ChipButton active={customMode} onClick={() => { setCustomMode(true); onChange(""); }}>
          ✏️ Other / Custom
        </ChipButton>
      )}
      {q.allow_custom && customMode && (
        <input
          data-testid={`profile-custom-${q.id}`}
          autoFocus
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your own answer..."
          className="brutal-input col-span-2 sm:col-span-3 text-sm"
        />
      )}
    </div>
  );
}

// Group a flat ordered question list into sections (by section name, first-appearance order).
function groupSections(questions) {
  const order = [];
  const map = {};
  for (const q of questions) {
    const key = q.section || "More About You";
    if (!map[key]) { map[key] = { icon: q.icon || "✨", title: key, questions: [] }; order.push(key); }
    map[key].questions.push(q);
  }
  return order.map((k) => map[k]);
}

export default function ProfileWizard({ me, token, onCompleted, onSkip }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => me?.profile_answers || {});
  const [submitting, setSubmitting] = useState(false);
  const [funTitle, setFunTitle] = useState(null);
  const [meterFill, setMeterFill] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/profile/questions")
      .then((r) => { if (alive) setSections(groupSections(r.data || [])); })
      .catch(() => { if (alive) toast.error("Couldn't load the profile questions."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const allQuestions = useMemo(() => sections.flatMap((s) => s.questions), [sections]);
  const totalQuestions = allQuestions.length;
  const totalSteps = sections.length;
  const onFinal = totalSteps > 0 && step >= totalSteps;
  const section = sections[step];

  // Animated fun meter on the final screen.
  useEffect(() => {
    if (!onFinal) return;
    let raf;
    const start = Date.now();
    const tick = () => {
      const elapsed = (Date.now() - start) / 1400;
      const pct = Math.min(100, Math.round(elapsed * 100));
      setMeterFill(pct);
      if (pct < 100) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [onFinal]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const answeredCount = useMemo(
    () => allQuestions.filter((q) => isAnswered(q, answers[q.id])).length,
    [answers, allQuestions]
  );

  const sectionAllAnswered = useMemo(() => {
    if (onFinal || !section) return true;
    return section.questions.every((q) => isAnswered(q, answers[q.id]));
  }, [answers, section, onFinal]);

  const setAnswer = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/employee/profile?token=${encodeURIComponent(token)}`, { answers });
      setFunTitle(r.data?.fun_title || "Office Avenger");
      setStep(totalSteps);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your profile, try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => { (step === totalSteps - 1) ? submit() : setStep((s) => s + 1); };
  const back = () => { if (step > 0) setStep((s) => s - 1); };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/85 backdrop-blur-sm" data-testid="profile-wizard">
      <div ref={scrollRef} className="w-full sm:max-w-3xl max-h-screen overflow-y-auto bg-[#FDFBF7] border-0 sm:border-4 border-black brutal-shadow">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#FDFBF7] border-b-4 border-black px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Welcome to the Fun Zone</div>
              <div className="font-black uppercase text-sm">Let's Get to Know You</div>
            </div>
          </div>
          <div className="text-xs font-mono text-neutral-600 hidden sm:block">
            {onFinal ? "🎊 Complete!" : `${answeredCount} / ${totalQuestions} answered`}
          </div>
          {onSkip && !onFinal && (
            <button data-testid="profile-skip" onClick={onSkip} title="Skip for now" className="p-1 border-2 border-black hover:bg-[#FFD600]">
              <X size={16}/>
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-neutral-200 border-b-4 border-black">
          <div className="h-full bg-[#39FF14] transition-all" style={{ width: `${onFinal ? 100 : (totalSteps ? Math.round((step / totalSteps) * 100) : 0)}%` }} />
        </div>

        {/* Body */}
        {loading ? (
          <div className="p-10 text-center font-black uppercase text-neutral-500" data-testid="profile-loading">Loading your questions…</div>
        ) : totalSteps === 0 ? (
          <div className="p-10 text-center space-y-4" data-testid="profile-empty">
            <div className="text-4xl">🎈</div>
            <p className="font-bold">No profile questions are set up yet.</p>
            <button data-testid="profile-empty-continue" onClick={onCompleted} className="brutal-btn inline-flex items-center gap-2"><Sparkles size={16}/> Continue</button>
          </div>
        ) : !onFinal ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.22 }}
              className="p-5 sm:p-8 space-y-6"
            >
              <div className="flex items-center gap-3">
                <span className="text-4xl" data-testid="profile-section-icon">{section.icon}</span>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                    Section {step + 1} of {totalSteps}
                  </div>
                  <h2 data-testid="profile-section-title" className="text-2xl sm:text-3xl font-black uppercase">{section.title}</h2>
                </div>
              </div>

              {step === 0 && (
                <p className="text-sm text-neutral-600 italic">
                  This will only take 2 minutes. There are no wrong answers... unless you say pineapple belongs on everything 😜
                </p>
              )}

              <div className="space-y-6">
                {section.questions.map((q, qi) => (
                  <div key={q.id} className="space-y-2" data-testid={`profile-question-${q.id}`}>
                    <label className="font-black uppercase text-sm block">
                      <span className="text-neutral-400 mr-1">{qi + 1}.</span> {q.label}
                    </label>
                    <QuestionField q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        ) : (
          <FinalScreen funTitle={funTitle} meterFill={meterFill} onContinue={onCompleted}/>
        )}

        {/* Footer nav */}
        {!loading && totalSteps > 0 && !onFinal && (
          <div className="sticky bottom-0 bg-[#FDFBF7] border-t-4 border-black px-5 py-3 flex items-center justify-between gap-3">
            <button
              data-testid="profile-back-btn"
              onClick={back}
              disabled={step === 0}
              className="brutal-btn-secondary flex items-center gap-2 text-sm disabled:opacity-40"
            >
              <ArrowLeft size={16}/> Back
            </button>
            <div className="text-xs font-mono text-neutral-500 sm:hidden">
              {answeredCount} / {totalQuestions}
            </div>
            <button
              data-testid={step === totalSteps - 1 ? "profile-finish-btn" : "profile-next-btn"}
              onClick={next}
              disabled={!sectionAllAnswered || submitting}
              className="brutal-btn flex items-center gap-2 text-sm disabled:opacity-40"
            >
              {submitting ? "Saving…" : (step === totalSteps - 1 ? "🎉 Finish" : <>Next <ArrowRight size={16}/></>)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FinalScreen({ funTitle, meterFill, onContinue }) {
  return (
    <div className="relative p-6 sm:p-10 space-y-6 text-center" data-testid="profile-final-screen">
      <Confetti/>
      <motion.div
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", bounce: 0.5 }}
      >
        <div className="text-6xl mb-2">🎊</div>
        <h2 className="text-3xl sm:text-4xl font-black uppercase">You're officially part of the Fun Zone!</h2>
      </motion.div>

      <div className="max-w-md mx-auto">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Your Fun Meter</div>
        <div className="border-4 border-black h-8 bg-white relative overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#39FF14] via-[#FFD600] to-[#FF3B30] transition-all duration-100"
            style={{ width: `${meterFill}%` }}
            data-testid="profile-fun-meter"
          />
          <span className="absolute inset-0 flex items-center justify-center text-sm font-black">
            {meterFill}%
          </span>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="inline-block border-4 border-black bg-[#FFD600] px-6 py-4 brutal-shadow rotate-[-2deg]"
        data-testid="profile-fun-title"
      >
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-700">You are</div>
        <div className="text-2xl sm:text-3xl font-black uppercase">{funTitle || "Office Avenger"}</div>
      </motion.div>

      <p className="max-w-lg mx-auto text-neutral-700">
        Awesome! Your Fun Profile is complete. Get ready to unlock games, challenges, quizzes, and bragging rights.
        <br/><strong>Let the fun begin! 🎉</strong>
      </p>

      <button
        data-testid="profile-celebration-continue"
        onClick={onContinue}
        className="brutal-btn inline-flex items-center gap-2 text-base"
      >
        <Sparkles size={18}/> Continue
      </button>
    </div>
  );
}
